import { useMemo, type ReactNode } from "react";
import styles from "./TopUpView.module.scss";
import { SavedCard } from "./SavedCard";
import { VisaIcon } from "../assets/VisaIcon";
import { MastercardIcon } from "../assets/MastercardIcon";
import { MIRIcon } from "../assets/MIRIcon";
import { AddCardIcon } from "../assets/AddCardIcon";
import { CardIcon } from "../assets/CardIcon";
import { useDraggableScroll } from "@/shared/hooks/useDraggableScroll";
import { cn } from "@/shared/lib/utils";

const cardIcons: Record<string, ReactNode> = {
  visa: <VisaIcon />,
  mastercard: <MastercardIcon />,
  mir: <MIRIcon />,
};

type SavedCardData = {
  payment_id: string;
  type: string;
  last4: string;
  method: string;
};

interface CardCarouselProps {
  onAddCardClick: () => void;
  savedCards: SavedCardData[];
  selectedCardId: string | null;
  onSelectCard: (id: string) => void;
  paymentMethod: "sbp" | "card";
  isLoading: boolean;
  isAddingCard: boolean;
}

export function CardCarousel({
  onAddCardClick,
  savedCards,
  selectedCardId,
  onSelectCard,
  paymentMethod,
  isLoading,
  isAddingCard,
}: CardCarouselProps) {
  const { ref, isDragging, events } = useDraggableScroll<HTMLDivElement>();

  const filteredPaymentMethods = useMemo(() => {
    if (!savedCards) {
      return [];
    }

    return savedCards.filter((method) => {
      if (paymentMethod === "sbp") {
        return method.method === "yookassa_sbp";
      }
      return method.method !== "yookassa_sbp";
    });
  }, [paymentMethod, savedCards]);

  const handleCardClick = (e: React.MouseEvent, cardId: string) => {
    if (Math.abs(e.movementX) < 2 && Math.abs(e.movementY) < 2) {
      if (!isDragging) {
        onSelectCard(cardId);
      }
    }
  };

  if (isLoading) {
    return (
      <div className={styles.cardCarousel}>
        {[...Array(2)].map((_, index) => (
          <div key={index} className={styles.savedCardSkeleton}></div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(styles.cardCarousel, { [styles.dragging]: isDragging })}
      {...events}
    >
      {filteredPaymentMethods.map((card) => (
        <SavedCard
          key={card.payment_id}
          icon={cardIcons[card.type] || <CardIcon />}
          last4={card.last4}
          isSelected={selectedCardId === card.payment_id}
          onClick={(e) => handleCardClick(e, card.payment_id)}
        />
      ))}
      <div className={styles.addCardButton} onClick={onAddCardClick}>
        {isAddingCard ? (
          <div className={styles.spinner} />
        ) : (
          <>
            <AddCardIcon />
            <span>Добавить</span>
          </>
        )}
      </div>
    </div>
  );
}
